// ============================================================================
// CHANGO V5.0 — D4 Ops Review Request Inbox
// ============================================================================
// Dual-control (two-person) review queue for sensitive ops:
//   - sku_pricing_publish
//   - admin_seat_grant
//   - admin_seat_revoke
//   - (future) feature_flag_toggle, workspace_admin_assume, ...
//
// §20 red lines enforced at both layers:
//   - Requester ≠ approver (UI hides Approve button; backend also blocks)
//   - Requester ≠ executor (future Apply page)
//   - Reason required for reject, 4..1000 chars
//   - Expired rows cannot be acted on
//
// Endpoints:
//   GET    /api/ops/review-requests?status=&kind=
//   POST   /api/ops/review-requests/:id/approve
//   POST   /api/ops/review-requests/:id/reject   { reason }
//   POST   /api/ops/review-requests/:id/cancel   (own requests only)
// ============================================================================

import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useAuth } from '../../hooks/useAuth.js';
import { api, HttpError } from '../../api/client.js';

const STATUS_META = {
  pending:   { label: '待审',  tone: 'bg-amber-200 text-amber-900' },
  approved:  { label: '已审',  tone: 'bg-emerald-200 text-emerald-900' },
  rejected:  { label: '已拒',  tone: 'bg-rose-200 text-rose-900' },
  cancelled: { label: '已撤',  tone: 'bg-slate-200 text-slate-700' },
  executed:  { label: '已执行', tone: 'bg-sky-200 text-sky-900' },
  expired:   { label: '已过期', tone: 'bg-slate-300 text-slate-700' }
};

// Canonical action_kind values match V5.0 backend — see
// server/lib/reviewRequestStateMachine.js VALID_ACTION_KINDS.
const KIND_LABEL = {
  sku_price_change:   'SKU 价格发布',
  admin_grant:        '授予 admin',
  admin_revoke:       '撤销 admin',
  workspace_archive:  '工作区归档',
  refund:             '退款',
  impersonate:        '工作区假冒登录'
};

// Per-kind dispatch → which backend endpoint the 执行 button POSTs to.
// Body is always `{ review_request_id: row.id }`. null → executor not wired.
function applyEndpointFor(row) {
  if (!row) return null;
  switch (row.action_kind) {
    case 'sku_price_change':
      if (!row.target_key) return null;
      return `/api/ops/sku-pricing/${encodeURIComponent(row.target_key)}/apply`;
    case 'admin_grant':
      return '/api/ops/admin-seats/grant/apply';
    case 'admin_revoke':
      return '/api/ops/admin-seats/revoke/apply';
    case 'workspace_archive':
      if (!row.target_id) return null;
      return `/api/ops/workspaces/${encodeURIComponent(row.target_id)}/archive`;
    default:
      return null;
  }
}

function formatTs(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString('zh-CN', { hour12: false });
  } catch {
    return iso;
  }
}

function StatusPill({ status }) {
  const m = STATUS_META[status] || { label: status || '—', tone: 'bg-slate-100 text-slate-700' };
  return (
    <span className={['inline-block rounded px-2 py-0.5 text-xs font-medium', m.tone].join(' ')}>
      {m.label}
    </span>
  );
}

function PayloadPreview({ kind, payload }) {
  // Minimal best-effort summariser; the row also exposes a "详情" toggle for
  // the full JSON. Keeping this terse so the queue stays scannable.
  if (!payload || typeof payload !== 'object') return <span className="text-slate-400">—</span>;
  try {
    switch (kind) {
      case 'sku_price_change':
        return (
          <span className="text-slate-600">
            {payload.sku_code || '—'}
            {payload.proposed_amount_rmb != null && (
              <> · ¥{Number(payload.proposed_amount_rmb).toLocaleString('zh-CN')}</>
            )}
          </span>
        );
      case 'admin_grant':
      case 'admin_revoke':
        return <span className="text-slate-600">{payload.target_email || payload.target_user_id || '—'}</span>;
      case 'workspace_archive':
        return <span className="text-slate-600">ws #{payload.workspace_id ?? '—'}</span>;
      default:
        return <span className="font-mono text-xs text-slate-500">{JSON.stringify(payload).slice(0, 60)}…</span>;
    }
  } catch {
    return <span className="text-slate-400">—</span>;
  }
}

function RejectDialog({ open, onClose, onSubmit, busy }) {
  const [reason, setReason] = useState('');
  useEffect(() => { if (open) setReason(''); }, [open]);
  if (!open) return null;

  const trimmed = reason.trim();
  const lenOk = trimmed.length >= 4 && trimmed.length <= 1000;

  return (
    <div className="fixed inset-0 z-30 flex items-center justify-center bg-slate-900/60" role="dialog" aria-modal="true">
      <div className="w-full max-w-md rounded-lg bg-white p-5 shadow-xl">
        <h3 className="text-base font-semibold text-slate-800">拒绝申请</h3>
        <p className="mt-1 text-xs text-slate-500">理由将写入审计日志，至少 4 个字符。</p>
        <textarea
          className="mt-3 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-chango-500 focus:outline-none"
          rows={4}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="请写清楚拒绝原因…"
          maxLength={1000}
        />
        <div className="mt-1 flex justify-between text-xs text-slate-400">
          <span>{trimmed.length}/1000</span>
          {!lenOk && trimmed.length > 0 && (
            <span className="text-rose-500">至少 4 个字符</span>
          )}
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded-md border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            取消
          </button>
          <button
            type="button"
            onClick={() => onSubmit(trimmed)}
            disabled={!lenOk || busy}
            className="rounded-md bg-rose-600 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-700 disabled:opacity-50"
          >
            {busy ? '处理中…' : '确认拒绝'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ReviewRow({ row, onAction, busy, currentUserId }) {
  const [expanded, setExpanded] = useState(false);
  // Canonical field names from pickReviewRow() in reviewRequests.js:
  //   requested_by / approved_by / requested_at / expires_at
  const requesterId = row.requested_by;
  const approverId  = row.approved_by;
  const isMine      = currentUserId != null && Number(requesterId) === Number(currentUserId);
  const isApprover  = currentUserId != null && approverId != null
                     && Number(approverId) === Number(currentUserId);
  const isExpired   = row.status === 'expired' ||
                      (row.expires_at && new Date(row.expires_at).getTime() < Date.now());
  const isTerminal  = ['rejected', 'cancelled', 'executed', 'expired'].includes(row.status);

  const canApprove  = !isTerminal && row.status === 'pending'  && !isMine && !isExpired;
  const canReject   = !isTerminal && (row.status === 'pending' || row.status === 'approved') && !isMine;
  const canCancel   = row.status === 'pending'  && isMine;
  // §20 #4 — executor must differ from requester AND approver.
  const executeUrl  = applyEndpointFor(row);
  const canExecute  = row.status === 'approved' && !isMine && !isApprover
                      && !isExpired && Boolean(executeUrl);

  return (
    <>
      <tr className="border-t border-slate-100 hover:bg-slate-50/50">
        <td className="px-4 py-3 font-mono text-xs">#{row.id}</td>
        <td className="px-4 py-3">
          <div className="font-medium text-slate-800">{KIND_LABEL[row.action_kind] || row.action_kind}</div>
          <div className="text-xs text-slate-500"><PayloadPreview kind={row.action_kind} payload={row.payload} /></div>
        </td>
        <td className="px-4 py-3 text-slate-600">
          {row.requester_email || (requesterId != null ? `#${requesterId}` : '—')}
        </td>
        <td className="px-4 py-3"><StatusPill status={row.status} /></td>
        <td className="px-4 py-3 text-slate-500">{formatTs(row.requested_at)}</td>
        <td className="px-4 py-3 text-slate-500">{formatTs(row.expires_at)}</td>
        <td className="px-4 py-3 text-right space-x-2 whitespace-nowrap">
          {canApprove && (
            <button
              type="button"
              onClick={() => onAction('approve', row)}
              disabled={busy}
              className="rounded bg-emerald-600 px-3 py-1 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
            >
              批准
            </button>
          )}
          {canExecute && (
            <button
              type="button"
              onClick={() => onAction('execute', row)}
              disabled={busy}
              className="rounded bg-sky-600 px-3 py-1 text-xs font-semibold text-white hover:bg-sky-700 disabled:opacity-50"
              title="执行此已批准的申请"
            >
              执行
            </button>
          )}
          {canReject && (
            <button
              type="button"
              onClick={() => onAction('reject', row)}
              disabled={busy}
              className="rounded border border-rose-300 px-3 py-1 text-xs font-semibold text-rose-700 hover:bg-rose-50 disabled:opacity-50"
            >
              拒绝
            </button>
          )}
          {canCancel && (
            <button
              type="button"
              onClick={() => onAction('cancel', row)}
              disabled={busy}
              className="rounded border border-slate-300 px-3 py-1 text-xs text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              撤回
            </button>
          )}
          {row.status === 'approved' && !executeUrl && (
            <span
              className="rounded bg-slate-200 px-2 py-1 text-xs text-slate-500"
              title="该类型尚未接入执行通道"
            >
              执行 (未接入)
            </span>
          )}
          <button
            type="button"
            onClick={() => setExpanded((x) => !x)}
            className="rounded px-2 py-1 text-xs text-slate-500 hover:text-slate-800"
          >
            {expanded ? '收起' : '详情'}
          </button>
        </td>
      </tr>
      {expanded && (
        <tr className="bg-slate-50/70">
          <td colSpan={7} className="px-4 py-3">
            <pre className="max-h-64 overflow-auto rounded border border-slate-200 bg-white p-3 text-xs text-slate-700">
{JSON.stringify(row, null, 2)}
            </pre>
          </td>
        </tr>
      )}
    </>
  );
}

function useReviewRequests(filters) {
  const [state, setState] = useState({ rows: [], total: 0, loading: true, error: null });
  const [reloadKey, setReloadKey] = useState(0);

  const reload = useCallback(() => setReloadKey((x) => x + 1), []);

  useEffect(() => {
    let cancelled = false;
    setState((s) => ({ ...s, loading: true, error: null }));
    const query = new URLSearchParams();
    if (filters.status) query.set('status', filters.status);
    // Backend GET /api/ops/review-requests filters with `?action=`, not `?kind=`.
    if (filters.kind)   query.set('action', filters.kind);
    query.set('limit', '100');

    (async () => {
      try {
        const res = await api.get(`/api/ops/review-requests?${query.toString()}`);
        if (cancelled) return;
        setState({
          rows: Array.isArray(res?.items) ? res.items : [],
          total: Number.isInteger(res?.total) ? res.total : 0,
          loading: false,
          error: null
        });
      } catch (err) {
        if (cancelled) return;
        if (err instanceof HttpError && err.status === 404) {
          setState({ rows: [], total: 0, loading: false, error: null });
        } else {
          setState({ rows: [], total: 0, loading: false, error: err.message || '加载失败' });
        }
      }
    })();
    return () => { cancelled = true; };
  }, [filters.status, filters.kind, reloadKey]);

  return { ...state, reload };
}

export default function OpsReviewInbox() {
  const { user } = useAuth();
  const [statusFilter, setStatusFilter] = useState('pending');
  const [kindFilter, setKindFilter] = useState('');
  const filters = useMemo(() => ({ status: statusFilter, kind: kindFilter }), [statusFilter, kindFilter]);
  const { rows, total, loading, error, reload } = useReviewRequests(filters);

  const [busyId, setBusyId] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [rejectRow, setRejectRow] = useState(null);

  const onAction = async (kind, row) => {
    setActionError(null);
    if (kind === 'reject') {
      setRejectRow(row);
      return;
    }
    setBusyId(row.id);
    try {
      if (kind === 'approve') {
        await api.post(`/api/ops/review-requests/${row.id}/approve`, {});
      } else if (kind === 'cancel') {
        await api.post(`/api/ops/review-requests/${row.id}/cancel`, {});
      } else if (kind === 'execute') {
        const url = applyEndpointFor(row);
        if (!url) {
          throw new Error(`${row.action_kind} 类型尚未接入执行通道`);
        }
        // Backend apply endpoints all expect { review_request_id } and re-check
        // status / executor-vs-approver / executor-vs-requester.
        await api.post(url, { review_request_id: row.id });
      }
      reload();
    } catch (err) {
      const detail = err instanceof HttpError
        ? `${err.code}: ${err.message}`
        : (err.message || '操作失败');
      setActionError(detail);
    } finally {
      setBusyId(null);
    }
  };

  const submitReject = async (reason) => {
    if (!rejectRow) return;
    setBusyId(rejectRow.id);
    setActionError(null);
    try {
      await api.post(`/api/ops/review-requests/${rejectRow.id}/reject`, { reason });
      setRejectRow(null);
      reload();
    } catch (err) {
      setActionError(err.message || '拒绝失败');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 text-slate-100">
      <header className="border-b border-slate-700 bg-slate-800/80">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <div>
            <h1 className="text-lg font-semibold">审批收件箱</h1>
            <p className="text-xs text-slate-400">
              {user?.email} · {user?.role} · 每一次批准 / 拒绝都会写入 <code>ops_action_log</code>
            </p>
          </div>
          <button
            type="button"
            onClick={reload}
            className="rounded border border-slate-600 px-3 py-1 text-xs text-slate-200 hover:bg-slate-700"
          >
            刷新
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-8">
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="rounded border border-slate-600 bg-slate-800 px-3 py-2 text-sm"
          >
            <option value="">全部状态</option>
            {Object.entries(STATUS_META).map(([k, v]) => (
              <option key={k} value={k}>{v.label}</option>
            ))}
          </select>
          <select
            value={kindFilter}
            onChange={(e) => setKindFilter(e.target.value)}
            className="rounded border border-slate-600 bg-slate-800 px-3 py-2 text-sm"
          >
            <option value="">全部动作</option>
            {Object.entries(KIND_LABEL).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
          <span className="ml-auto text-xs text-slate-400">共 {total} 条</span>
        </div>

        {(error || actionError) && (
          <div className="mb-4 rounded-md border border-rose-500/40 bg-rose-900/30 px-4 py-2 text-sm text-rose-200">
            {error || actionError}
          </div>
        )}

        <div className="overflow-hidden rounded-lg border border-slate-700 bg-slate-800/40">
          <table className="w-full text-sm">
            <thead className="bg-slate-800/80 text-left text-xs font-medium uppercase tracking-wide text-slate-400">
              <tr>
                <th className="px-4 py-3">编号</th>
                <th className="px-4 py-3">动作 / 内容</th>
                <th className="px-4 py-3">申请人</th>
                <th className="px-4 py-3">状态</th>
                <th className="px-4 py-3">申请时间</th>
                <th className="px-4 py-3">到期时间</th>
                <th className="px-4 py-3 text-right">操作</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400">加载中…</td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400">队列里没有待审项</td></tr>
              )}
              {!loading && rows.map((r) => (
                <ReviewRow
                  key={r.id}
                  row={r}
                  onAction={onAction}
                  busy={busyId === r.id}
                  currentUserId={user?.id}
                />
              ))}
            </tbody>
          </table>
        </div>

        <p className="mt-6 text-center text-xs text-slate-500">
          §20 #4 两人复核：申请人 / 批准人 / 执行人 三角色不可重合。
        </p>
      </main>

      <RejectDialog
        open={Boolean(rejectRow)}
        onClose={() => setRejectRow(null)}
        onSubmit={submitReject}
        busy={busyId === rejectRow?.id}
      />
    </div>
  );
}
